/**
 * WebSocket Redux Slice
 * Управление потоками данных и аналитикой WebSocket
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  RealtimeDataStream,
  CreateRealtimeStreamDto,
  UpdateRealtimeStreamDto,
  BalanceStreamsRequest,
  StreamStatisticsResponse,
  GetConnectionsRequest,
  ConnectionsStatisticsResponse,
  SubscriptionsStatisticsResponse,
  MessagesStatisticsResponse,
  StreamsStatisticsResponse,
  UserActivityResponse,
  GetTopUsersRequest,
  TopUsersResponse,
  TopChannelsResponse,
} from '../types/websocket';
import {
  createRealtimeStream,
  getActiveStreams,
  updateRealtimeStream,
  deleteRealtimeStream,
  pauseRealtimeStream,
  resumeRealtimeStream,
  balanceStreams,
  getStreamStatistics,
  getConnectionsStatistics,
  getSubscriptionsStatistics,
  getMessagesStatistics,
  getStreamsStatistics,
  getUserActivity,
  getTopUsers,
  getTopChannels,
} from '../services/api/websocket.api';
import { logger } from '../utils/logger';

// Состояние WebSocket
export interface WebSocketState {
  streams: RealtimeDataStream[];
  activeStreams: RealtimeDataStream[];
  statistics: StreamStatisticsResponse | null;
  analytics: {
    connections: ConnectionsStatisticsResponse | null;
    subscriptions: SubscriptionsStatisticsResponse | null;
    messages: MessagesStatisticsResponse | null;
    streams: StreamsStatisticsResponse | null;
    userActivity: UserActivityResponse | null;
    topUsers: TopUsersResponse | null;
    topChannels: TopChannelsResponse | null;
  };
  loading: {
    streams: boolean;
    statistics: boolean;
    analytics: boolean;
  };
  error: string | null;
}

// Начальное состояние
const initialState: WebSocketState = {
  streams: [],
  activeStreams: [],
  statistics: null,
  analytics: {
    connections: null,
    subscriptions: null,
    messages: null,
    streams: null,
    userActivity: null,
    topUsers: null,
    topChannels: null,
  },
  loading: {
    streams: false,
    statistics: false,
    analytics: false,
  },
  error: null,
};

// ===== ASYNC THUNKS =====

// Управление потоками
export const createRealtimeStreamThunk = createAsyncThunk(
  'websocket/createStream',
  async (payload: CreateRealtimeStreamDto) => {
    try {
      const stream = await createRealtimeStream(payload);
      return stream;
    } catch (error) {
      logger.error('Ошибка создания потока данных:', error);
      throw error;
    }
  }
);

export const getActiveStreamsThunk = createAsyncThunk(
  'websocket/getActiveStreams',
  async () => {
    try {
      const streams = await getActiveStreams();
      return streams;
    } catch (error) {
      logger.error('Ошибка получения активных потоков:', error);
      throw error;
    }
  }
);

export const updateRealtimeStreamThunk = createAsyncThunk(
  'websocket/updateStream',
  async ({ streamId, payload }: { streamId: string; payload: UpdateRealtimeStreamDto }) => {
    try {
      const stream = await updateRealtimeStream(streamId, payload);
      return stream;
    } catch (error) {
      logger.error('Ошибка обновления потока данных:', error);
      throw error;
    }
  }
);

export const deleteRealtimeStreamThunk = createAsyncThunk(
  'websocket/deleteStream',
  async (streamId: string) => {
    try {
      await deleteRealtimeStream(streamId);
      return streamId;
    } catch (error) {
      logger.error('Ошибка удаления потока данных:', error);
      throw error;
    }
  }
);

export const pauseRealtimeStreamThunk = createAsyncThunk(
  'websocket/pauseStream',
  async (streamId: string) => {
    try {
      const stream = await pauseRealtimeStream(streamId);
      return stream;
    } catch (error) {
      logger.error('Ошибка паузы потока данных:', error);
      throw error;
    }
  }
);

export const resumeRealtimeStreamThunk = createAsyncThunk(
  'websocket/resumeStream',
  async (streamId: string) => {
    try {
      const stream = await resumeRealtimeStream(streamId);
      return stream;
    } catch (error) {
      logger.error('Ошибка возобновления потока данных:', error);
      throw error;
    }
  }
);

export const balanceStreamsThunk = createAsyncThunk(
  'websocket/balanceStreams',
  async (params?: BalanceStreamsRequest) => {
    try {
      const streams = await balanceStreams(params);
      return streams;
    } catch (error) {
      logger.error('Ошибка балансировки потоков:', error);
      throw error;
    }
  }
);

export const getStreamStatisticsThunk = createAsyncThunk(
  'websocket/getStreamStatistics',
  async () => {
    try {
      const statistics = await getStreamStatistics();
      return statistics;
    } catch (error) {
      logger.error('Ошибка получения статистики потоков:', error);
      throw error;
    }
  }
);

// Аналитика
export const getConnectionsStatisticsThunk = createAsyncThunk(
  'websocket/getConnectionsStatistics',
  async (params?: GetConnectionsRequest) => {
    try {
      const statistics = await getConnectionsStatistics(params);
      return statistics;
    } catch (error) {
      logger.error('Ошибка получения статистики подключений:', error);
      throw error;
    }
  }
);

export const getSubscriptionsStatisticsThunk = createAsyncThunk(
  'websocket/getSubscriptionsStatistics',
  async () => {
    try {
      const statistics = await getSubscriptionsStatistics();
      return statistics;
    } catch (error) {
      logger.error('Ошибка получения статистики подписок:', error);
      throw error;
    }
  }
);

export const getMessagesStatisticsThunk = createAsyncThunk(
  'websocket/getMessagesStatistics',
  async () => {
    try {
      const statistics = await getMessagesStatistics();
      return statistics;
    } catch (error) {
      logger.error('Ошибка получения статистики сообщений:', error);
      throw error;
    }
  }
);

export const getStreamsStatisticsThunk = createAsyncThunk(
  'websocket/getStreamsStatistics',
  async () => {
    try {
      const statistics = await getStreamsStatistics();
      return statistics;
    } catch (error) {
      logger.error('Ошибка получения статистики потоков (аналитика):', error);
      throw error;
    }
  }
);

export const getUserActivityThunk = createAsyncThunk(
  'websocket/getUserActivity',
  async () => {
    try {
      const activity = await getUserActivity();
      return activity;
    } catch (error) {
      logger.error('Ошибка получения активности пользователя:', error);
      throw error;
    }
  }
);

export const getTopUsersThunk = createAsyncThunk(
  'websocket/getTopUsers',
  async (params?: GetTopUsersRequest) => {
    try {
      const topUsers = await getTopUsers(params);
      return topUsers;
    } catch (error) {
      logger.error('Ошибка получения топ пользователей:', error);
      throw error;
    }
  }
);

export const getTopChannelsThunk = createAsyncThunk(
  'websocket/getTopChannels',
  async () => {
    try {
      const topChannels = await getTopChannels();
      return topChannels;
    } catch (error) {
      logger.error('Ошибка получения топ каналов:', error);
      throw error;
    }
  }
);

// Создаем slice
const websocketSlice = createSlice({
  name: 'websocket',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    removeStream: (state, action: PayloadAction<string>) => {
      state.streams = state.streams.filter(s => s.id !== action.payload);
      state.activeStreams = state.activeStreams.filter(s => s.id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    // ===== УПРАВЛЕНИЕ ПОТОКАМИ =====
    
    builder.addCase(createRealtimeStreamThunk.fulfilled, (state, action) => {
      state.streams.push(action.payload);
      if (action.payload.status === 'active') {
        state.activeStreams.push(action.payload);
      }
    });
    
    builder.addCase(getActiveStreamsThunk.pending, (state) => {
      state.loading.streams = true;
      state.error = null;
    });
    
    builder.addCase(getActiveStreamsThunk.fulfilled, (state, action) => {
      state.loading.streams = false;
      state.activeStreams = action.payload;
      // Обновляем потоки в общем списке
      action.payload.forEach(stream => {
        const index = state.streams.findIndex(s => s.id === stream.id);
        if (index !== -1) {
          state.streams[index] = stream;
        } else {
          state.streams.push(stream);
        }
      });
    });
    
    builder.addCase(getActiveStreamsThunk.rejected, (state, action) => {
      state.loading.streams = false;
      state.error = action.error.message || 'Ошибка получения активных потоков';
    });
    
    builder.addCase(updateRealtimeStreamThunk.fulfilled, (state, action) => {
      const index = state.streams.findIndex(s => s.id === action.payload.id);
      if (index !== -1) {
        state.streams[index] = action.payload;
      }
      const activeIndex = state.activeStreams.findIndex(s => s.id === action.payload.id);
      if (action.payload.status === 'active') {
        if (activeIndex === -1) {
          state.activeStreams.push(action.payload);
        } else {
          state.activeStreams[activeIndex] = action.payload;
        }
      } else if (activeIndex !== -1) {
        state.activeStreams.splice(activeIndex, 1);
      }
    });
    
    builder.addCase(deleteRealtimeStreamThunk.fulfilled, (state, action) => {
      state.streams = state.streams.filter(s => s.id !== action.payload);
      state.activeStreams = state.activeStreams.filter(s => s.id !== action.payload);
    });
    
    builder.addCase(pauseRealtimeStreamThunk.fulfilled, (state, action) => {
      const index = state.streams.findIndex(s => s.id === action.payload.id);
      if (index !== -1) {
        state.streams[index] = action.payload;
      }
      state.activeStreams = state.activeStreams.filter(s => s.id !== action.payload.id);
    });
    
    builder.addCase(resumeRealtimeStreamThunk.fulfilled, (state, action) => {
      const index = state.streams.findIndex(s => s.id === action.payload.id);
      if (index !== -1) {
        state.streams[index] = action.payload;
      }
      if (action.payload.status === 'active') {
        const activeIndex = state.activeStreams.findIndex(s => s.id === action.payload.id);
        if (activeIndex === -1) {
          state.activeStreams.push(action.payload);
        }
      }
    });
    
    builder.addCase(balanceStreamsThunk.fulfilled, (state, action) => {
      state.activeStreams = action.payload;
      // Обновляем потоки в общем списке
      action.payload.forEach(stream => {
        const index = state.streams.findIndex(s => s.id === stream.id);
        if (index !== -1) {
          state.streams[index] = stream;
        }
      });
    });
    
    builder.addCase(getStreamStatisticsThunk.pending, (state) => {
      state.loading.statistics = true;
    });
    
    builder.addCase(getStreamStatisticsThunk.fulfilled, (state, action) => {
      state.loading.statistics = false;
      state.statistics = action.payload;
    });
    
    builder.addCase(getStreamStatisticsThunk.rejected, (state, action) => {
      state.loading.statistics = false;
      state.error = action.error.message || 'Ошибка получения статистики потоков';
    });
    
    // ===== АНАЛИТИКА =====
    
    builder.addCase(getConnectionsStatisticsThunk.fulfilled, (state, action) => {
      state.analytics.connections = action.payload;
    });
    
    builder.addCase(getSubscriptionsStatisticsThunk.fulfilled, (state, action) => {
      state.analytics.subscriptions = action.payload;
    });
    
    builder.addCase(getMessagesStatisticsThunk.fulfilled, (state, action) => {
      state.analytics.messages = action.payload;
    });
    
    builder.addCase(getStreamsStatisticsThunk.fulfilled, (state, action) => {
      state.analytics.streams = action.payload;
    });
    
    builder.addCase(getUserActivityThunk.fulfilled, (state, action) => {
      state.analytics.userActivity = action.payload;
    });
    
    builder.addCase(getTopUsersThunk.fulfilled, (state, action) => {
      state.analytics.topUsers = action.payload;
    });
    
    builder.addCase(getTopChannelsThunk.fulfilled, (state, action) => {
      state.analytics.topChannels = action.payload;
    });
  },
});

export const { clearError, removeStream } = websocketSlice.actions;

export default websocketSlice.reducer;

